import React, { useEffect, useMemo, useState } from 'react';

import Select, { SelectOption } from '@amzn/meridian/select';
import _ from 'lodash';
import { useAppSelector } from 'src/store/store';
import { isNotEmpty } from 'src/utils/helpers';
import {
  isTimeRestrictionTask,
  isWeightRestrictionChildTask,
  type RestrictionListTask,
  setupShiftLength,
} from 'src/utils/restrictionUtils';

type HoursSelectorOption = { label: string; value: number };

interface HoursSelectorState {
  options: HoursSelectorOption[];
  selectedOptionLabel: string | undefined;
  shiftLength: number;
}

const getZeroHoursLabel = (taskName: string) => `0 hrs (Associate cannot ${taskName})`;

/**
 * Select hours value available based on the shift length.
 */
const HoursSelector = <T extends RestrictionListTask>(props: {
  task: T;
  enableFullShift?: boolean;
  onChange: (task: T) => void;
  listIndex: number;
  childIndex?: number;
}) => {
  const { task, enableFullShift, onChange, listIndex, childIndex } = props;
  const { backgroundFormData, isRestrictionsPageReadOnly } = useAppSelector((state) => state.newJmrPage);

  // State
  const [state, setState] = useState<HoursSelectorState>({
    options: [],
    selectedOptionLabel: undefined,
    shiftLength: 0,
  });

  // Set the options based on the shift length
  useEffect(() => {
    // Setup options
    const options: HoursSelectorOption[] = [];
    if (!isWeightRestrictionChildTask(task)) {
      const firstLabel = getZeroHoursLabel(task.label);
      options.push({ label: firstLabel, value: 0 });
    }
    const shiftLength = setupShiftLength(backgroundFormData);
    for (let i = 1; i < shiftLength; i++) {
      options.push({ label: `${i} hrs`, value: i });
    }
    // If full shift is enabled
    if (enableFullShift) {
      options.push({ label: `${shiftLength} hrs (Full Shift)`, value: shiftLength });
    }
    // Check if pre-existing value, which could happen if reloading after deleting another
    // Restriction Item Box, or navigating back to the Restrictions page from another page.
    let selectedOptionLabel;
    if (isNotEmpty(task.restrictedValue)) {
      const opt = options.find((o) => o.value === Number(task.restrictedValue));
      selectedOptionLabel = opt?.label;
      // If not existing value and full-shift is enabled, default to full shift
    } else if (enableFullShift) {
      const opt = options.find((o) => o.value === shiftLength);
      selectedOptionLabel = opt?.label;
    }
    // Update state
    setState({ options, selectedOptionLabel, shiftLength });
  }, [backgroundFormData, task.taskId, task.restrictedValue]);

  // Listen for dropdown menu updates
  const onOptionChange = (value: string) => {
    setState({ ...state, selectedOptionLabel: value });
    // If the restrictedValue is the shiftlength then this task is not considered a
    // restriction and should not factor into the job match result logic.
    const selectedOption = state.options.find((o) => o.label === value);
    if (selectedOption?.value === state.shiftLength) {
      const taskNew = _.cloneDeep(task);
      delete taskNew.restrictedValue;
      delete taskNew.error;
      onChange(taskNew);
      // Update the task's restricted value when selection changes
    } else {
      const newVal = String(selectedOption?.value);
      if (newVal !== task.restrictedValue) {
        const taskNew = _.cloneDeep(task);
        delete taskNew.error;
        if (isTimeRestrictionTask(taskNew) && !!taskNew.taskValueError) {
          delete taskNew.taskValueError;
        }
        taskNew.restrictedValue = newVal;
        onChange(taskNew);
      }
    }
  };

  // Initialize the error message. Note the priority of the error messages
  const errorMsg: string = useMemo(() => {
    if (isTimeRestrictionTask(task) && !!task.taskValueError) {
      return task.taskValueError;
    } else if (!!task.error) {
      return task.error;
    } else {
      return '';
    }
  }, [task]);

  return (
    <>
      <Select
        data-cy={
          childIndex
            ? `new-jmr__restrictions__hours-selector__dropdown__${listIndex}__${childIndex}`
            : `new-jmr__restrictions__hours-selector__dropdown__${listIndex}`
        }
        placeholder="Select hours"
        value={state.selectedOptionLabel}
        onChange={(v) => onOptionChange(v)}
        errorMessage={errorMsg}
        error={!!errorMsg}
        disabled={isRestrictionsPageReadOnly}
      >
        {state.options.map((option, optionIndex) => (
          <SelectOption
            label={option.label}
            value={option.label}
            key={optionIndex}
            data-cy="new-jmr__hours-selector__dropdown-item"
            data-testid="new-jmr__hours-selector__dropdown-item"
          />
        ))}
      </Select>
    </>
  );
};

export default HoursSelector;
